// skipcq: JS-W1028, JS-0128
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import BeerCard from '../../components/beer/BeerCard';

/**
 * BeerListDisplay
 * Displays a list of beers in a grid layout with brewery action controls.
 */
function BeerListDisplay({
  beers,
  loading,
  error,
  styleMap,
  onAddBeer,
  selectedBeerIds,
  onToggleBeerSelect,
  onDeleteClick,
  deleteDisabled,
  onEditBeer,
  onMergeClick,
  mergeDisabled,
  onSourcesClick,
  onAddToEvent,
  addToEventDisabled,
  onShadowProfileClick,
  shadowProfileSummariesByBeerId,
  shadowDiscoverabilityControl,
  breweryFilterControl,
  breweryNameById,
  groupedBreweryIds,
  locationLabelsError,
}) {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 2,
        }}
      >
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 48,
          zIndex: (theme) => theme.zIndex.appBar - 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1,
          px: 2,
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          {breweryFilterControl}
          {shadowDiscoverabilityControl}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {onAddBeer ? (
            <Button variant="contained" color="primary" onClick={onAddBeer}>
              Add Beer
            </Button>
          ) : null}
          {onSourcesClick ? (
            <Button variant="contained" color="primary" onClick={onSourcesClick}>
              Sources
            </Button>
          ) : null}
          {onMergeClick ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={onMergeClick}
              disabled={mergeDisabled}
            >
              Merge Beers
            </Button>
          ) : null}
          {onAddToEvent ? (
            <Button
              variant="contained"
              color="primary"
              onClick={onAddToEvent}
              disabled={addToEventDisabled}
            >
              Add to Event
            </Button>
          ) : null}
          {onDeleteClick ? (
            <Button
              variant="contained"
              color="error"
              onClick={onDeleteClick}
              disabled={deleteDisabled}
            >
              Delete Beers
            </Button>
          ) : null}
        </Box>
      </Box>
      {locationLabelsError ? (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="body2" color="error">
            {locationLabelsError}
          </Typography>
        </Box>
      ) : null}
      {!beers.length ? (
        <Box
          sx={{
            p: 2,
          }}
        >
          <Typography variant="body2">No beers found.</Typography>
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ pt: 2 }}>
          {beers.map((beer) => (
            <Grid item xs={12} sm={6} md={4} key={beer.id}>
              <BeerCard
                beer={beer}
                styleMap={styleMap}
                selected={
                  Array.isArray(selectedBeerIds) &&
                  selectedBeerIds.includes(beer.id)
                }
                onToggleSelect={onToggleBeerSelect}
                onEdit={onEditBeer}
                onShadowProfile={onShadowProfileClick}
                shadowProfileSummary={
                  shadowProfileSummariesByBeerId
                    ? shadowProfileSummariesByBeerId.get(beer.id)
                    : null
                }
                breweryNameById={breweryNameById}
                groupedBreweryIds={groupedBreweryIds}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}

BeerListDisplay.propTypes = {
  beers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  styleMap: PropTypes.object.isRequired,
  onAddBeer: PropTypes.func,
  selectedBeerIds: PropTypes.arrayOf(PropTypes.string),
  onToggleBeerSelect: PropTypes.func,
  onDeleteClick: PropTypes.func,
  deleteDisabled: PropTypes.bool,
  onEditBeer: PropTypes.func,
  onMergeClick: PropTypes.func,
  mergeDisabled: PropTypes.bool,
  onSourcesClick: PropTypes.func,
  onAddToEvent: PropTypes.func,
  addToEventDisabled: PropTypes.bool,
  onShadowProfileClick: PropTypes.func,
  shadowProfileSummariesByBeerId: PropTypes.instanceOf(Map),
  shadowDiscoverabilityControl: PropTypes.node,
  breweryFilterControl: PropTypes.node,
  breweryNameById: PropTypes.objectOf(PropTypes.string),
  groupedBreweryIds: PropTypes.objectOf(PropTypes.bool),
  locationLabelsError: PropTypes.string,
};

BeerListDisplay.defaultProps = {
  error: null,
  selectedBeerIds: [],
  onAddBeer: null,
  onToggleBeerSelect: null,
  onDeleteClick: null,
  deleteDisabled: true,
  onEditBeer: null,
  onMergeClick: null,
  mergeDisabled: true,
  onSourcesClick: null,
  onAddToEvent: null,
  addToEventDisabled: true,
  onShadowProfileClick: null,
  shadowProfileSummariesByBeerId: null,
  shadowDiscoverabilityControl: null,
  breweryFilterControl: null,
  breweryNameById: null,
  groupedBreweryIds: null,
  locationLabelsError: null,
};

export default BeerListDisplay;
